import React, { useEffect } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { useTranslation } from 'react-i18next';

import { ButtonRow, useAuth, useTrackedFormik } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../layout/Portal';
import { useAppSelector, useAppDispatch } from '../../../../../../store';
import { withdrawParticipantThunk } from '../../../../../../store/participant';
import {
  hasWithdrawalErrorSelector,
  isBusySelector,
  withdrawalValuesSelector,
  withdrawalResultSelector,
} from '../../../../../../store/participant/selectors';
import { WithdrawalOption } from '../../../../../../core/types/participant.types';
import { chartColours } from '../../../../../../core/constants/colours.constants';

function WithdrawalConfirm2Scene() {
  const dispatch = useAppDispatch();
  const { signOut } = useAuth();

  const isBusy = useAppSelector(isBusySelector);
  const hasError = useAppSelector(hasWithdrawalErrorSelector);
  const withdrawalValues = useAppSelector(withdrawalValuesSelector);
  const withdrawalResult = useAppSelector(withdrawalResultSelector);

  const { t } = useTranslation('portal');

  const formik = useTrackedFormik(
    {
      initialValues: {
        ...withdrawalValues,
      },
      onSubmit: (values) => {
        dispatch(withdrawParticipantThunk(values));
      },
    },
    'withdrawal-confirm-2',
  );

  useEffect(() => {
    if (withdrawalResult?.withdrawalOption === WithdrawalOption.COMPLETE) {
      signOut();
    }
  }, [withdrawalResult, signOut]);

  if (withdrawalResult?.withdrawalOption === WithdrawalOption.COMPLETE) {
    return null;
  }

  return (
    <PortalLayout>
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              pt: { xs: 2.5, md: 3.5 },
              pb: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${chartColours[5]}26`,
                  color: chartColours[5],
                  flexShrink: 0,
                  border: 1,
                  borderColor: `${chartColours[5]}55`,
                }}
              >
                <WarningAmberOutlined sx={{ fontSize: 34 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.15,
                  }}
                >
                  {t('withdrawal5.title')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <form onSubmit={formik.handleSubmit}>
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  mb: withdrawalResult ? 0 : 3,
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, md: 2.5 },
                  }}
                >
                  {!withdrawalResult && (
                    <>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          mb: 1.5,
                          fontWeight: 900,
                          lineHeight: 1.2,
                        }}
                      >
                        {t('withdrawal5.subtitle')}
                      </Typography>

                      <Typography
                        variant="body1"
                        component="p"
                        color="text.secondary"
                        sx={{
                          mb: 2.5,
                          maxWidth: 760,
                          lineHeight: 1.6,
                        }}
                      >
                        {t('withdrawal5.para1')}
                      </Typography>
                    </>
                  )}

                  {hasError && (
                    <Alert
                      severity="error"
                      icon={<ErrorOutlineOutlined />}
                      sx={{
                        borderRadius: 3,
                        border: 1,
                        borderColor: 'error.light',
                        alignItems: 'center',
                        '& .MuiAlert-message': {
                          fontWeight: 700,
                        },
                      }}
                    >
                      {t('withdrawal5.error')}
                    </Alert>
                  )}

                  {withdrawalResult && (
                    <Alert
                      severity="success"
                      icon={<CheckCircleOutlineOutlined />}
                      sx={{
                        borderRadius: 3,
                        border: 1,
                        borderColor: 'success.light',
                        alignItems: 'center',
                        '& .MuiAlert-message': {
                          fontWeight: 700,
                        },
                      }}
                    >
                      {t('withdrawal5.success')}
                    </Alert>
                  )}
                </Box>
              </Paper>

              {!withdrawalResult && (
                <Box
                  sx={{
                    '& button': {
                      borderRadius: 999,
                      fontWeight: 900,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ButtonRow
                    backLabel={t('withdrawal5.back')}
                    forwardLabel={t('withdrawal5.button')}
                    forwardColor="error"
                    buttonSize="large"
                    isDisabled={isBusy}
                  />
                </Box>
              )}
            </form>
          </Box>
        </Paper>
      </Box>
    </PortalLayout>
  );
}

export default WithdrawalConfirm2Scene;
